import { defineComponent as _defineComponent } from 'vue'
import { useContext, useStore, ref, computed } from '@nuxtjs/composition-api';
import { State } from '~/store';
import ActionButtons from '~/components/resource/ActionButtons.vue';
import DownloadButton from '~/components/resource/DownloadButton.vue';
import CustomTextForm from '~/components/resource/CustomTextForm.vue';
import ResourceHeaderProperty from '~/components/resource/ResourceHeaderProperty.vue';
import { convertToTitleCase, pluralise, convertToSentenceCase } from '~/utils/formatStrings';
import { curriculumNames } from '~/data/curriculum_names';
import { findFileTypeString, Resource, isEditableResource } from '~/types/resource';
import PreviewButton from '~/components/resource/PreviewButton.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ResourceHeader',
  props: {
    resource: null
  },
  setup(__props: any) {

const props = __props as {
  resource: Resource;
};

const { i18n } = useContext();


const customTextDownload = ref('');
const store = useStore<State>();
const isAuthenticated = computed(() => store.getters.isAuthenticated);
const userDownloadPermissions = computed(() => store.getters.userDownloadPermissions);
const isDifferentaited = computed(() => {
  if (props.resource.tags) {
    return Object.values(props.resource.tags).some(v => v.slug === 'differentiated');
  }
  return false;
});

const canUserDownloadFirstItem = computed(() => {
  return (
    userDownloadPermissions.value?.length > 0 && userDownloadPermissions.value[0].status === 200
  );
});
const nonEditableFileTypes = computed(() => {
  const fileTypes = props.resource.files
    .filter(file => {
      return file.downloadable_file_type === 'not editable' && file.format;
    })
    .map(file => findFileTypeString(file.format));
  return [...new Set(fileTypes)].join(', ');
});

const editableFileTypes = computed(() => {
  const fileTypes = props.resource.files
    .filter(file => {
      return file.downloadable_file_type === 'editable' && file.format;
    })
    .map(file => findFileTypeString(file.format));
  return [...new Set(fileTypes)].join(', ');
});
const resourceCurriculums = computed(() => {
  return props.resource.curriculums
    .map(curriculum => (curriculum.name ? curriculumNames[curriculum.name] : false))
    .filter(Boolean)
    .join(', ');
});
const formatYearLevels = computed(() => {
  return i18n.locale === 'us'
    ? props.resource.year_levels_range.replace('P ', 'PK')
    : props.resource.year_levels_range.replace('Preschool/Kindergarten', 'K');
});
const customisableTitleCase = computed(() => {
  return convertToTitleCase(i18n.tc('customizable'));
});
const pluralisePageCount = computed(() => {
  return `${props.resource.page_count} ${convertToSentenceCase(
    pluralise('page', props.resource.page_count ?? 0)
  )} `;
});
// Check that resource has a letter set tag, has one file that is a pdf and is at least 62 pages (this covers a-z, A-Z, 0-9)
const checkAllowsCustomText = computed(() => {
  if (props.resource.tags) {
    return Object.values(props.resource.tags).some(v =>
      v.name === 'Printable Letters' &&
      props.resource.files.length === 1 &&
      props.resource.files[0].format === 'pdf' &&
      props.resource.page_count
        ? props.resource.page_count >= 62
        : 0
    );
  }
  return false;
});

const gradeLabel = computed(() => {
  return props.resource?.year_levels_range?.length > 1
    ? i18n.tc('grade_label') + 's'
    : i18n.tc('grade_label');
});

const formatUpdatedDate = computed(() => {
  const modifiedDate = new Date(props.resource.modified);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const day = modifiedDate.getDate().toString().padStart(2, '0');
  const month = months[modifiedDate.getMonth()];
  const year = modifiedDate.getFullYear();
  return `${day} ${month} ${year}`;
});

function updateCustomText(customText: string) {
  customTextDownload.value = customText;
}

return { __sfc: true,i18n, props, customTextDownload, store, isAuthenticated, userDownloadPermissions, isDifferentaited, canUserDownloadFirstItem, nonEditableFileTypes, editableFileTypes, resourceCurriculums, formatYearLevels, customisableTitleCase, pluralisePageCount, checkAllowsCustomText, gradeLabel, formatUpdatedDate, updateCustomText, ActionButtons, DownloadButton, CustomTextForm, ResourceHeaderProperty, isEditableResource, PreviewButton }
}

})